import { getProductShipping } from "@/api/product";
import { ProductShipping } from "@/api/product/models";
import clsx from "clsx";
import moment from "moment-timezone";
import { useState, type JSX } from "react";
import { LoadingSpinner, formatCurrency, toast, useDebouncedSearch } from "shared-components";

interface Props {
  className?: string;
  productId: string;
  amount?: number;
}

const DeliveryInfo = ({
  label,
  value,
  loading,
  className,
}: {
  label: string;
  value?: string | null;
  loading?: boolean;
  className?: string;
}) => {
  return (
    <div className={clsx("flex items-center", className)}>
      <span className="mr-[.375rem]">{label}</span>

      {loading ? <LoadingSpinner size="1rem" /> : <b className="text-brand-primary">{value || "-"}</b>}
    </div>
  );
};

const formatPostalCode = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, "$1-$2");
};

const formatDeliveryPeriod = (delivery: ProductShipping) => {
  const deliveryDate = moment().add(delivery.deliveryTime, "days");
  return `${deliveryDate.format("D [de] MMMM")}`;
};

const inputClassName = clsx("w-auto shrink-0 grow px-2 py-0.5 text-center");

export const ProductDeliveryPeriod = ({ className, productId, amount }: Props): JSX.Element => {
  const [postalCode, setPostalCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [delivery, setDelivery] = useState<ProductShipping | null>(null);
  const [error, setError] = useState<string | null>(null);

  const debouncedAddressSearch = useDebouncedSearch((postalCode) => {
    if (postalCode.length === 9) {
      setLoading(true);
      setError(null);
      setDelivery(null);

      getProductShipping({ productId, postCode: postalCode, amount })
        .then((res: ProductShipping) => {
          if (res.location === "undefined - Brasil" || !res.location) {
            setError("CEP não encontrado");
            toast.error("CEP não encontrado");
            return;
          }
          setDelivery(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  });

  return (
    <div className={className}>
      <p className="font-bold">Prazo de entrega</p>

      <div className="mt-2 flex gap-2">
        <input
          aria-label="CEP"
          className={clsx(inputClassName, "max-w-[8.5rem]")}
          value={postalCode}
          placeholder="00000-000"
          inputMode="numeric"
          onChange={(e) => {
            const formattedPostalCode = formatPostalCode(e.target.value);

            setPostalCode(formattedPostalCode);
            debouncedAddressSearch(formattedPostalCode);
          }}
          disabled={loading}
        />
      </div>

      <DeliveryInfo
        className="mt-2 lg:mt-3"
        label="Entrega para"
        value={error ?? (delivery && `${delivery.location}`)}
        loading={loading}
      />

      <DeliveryInfo label="Estimada para" value={delivery && formatDeliveryPeriod(delivery)} loading={loading} />

      <DeliveryInfo
        className="mt-2 lg:mt-3"
        label="Frete a partir de"
        value={delivery && formatCurrency(delivery.price)}
        loading={loading}
      />
    </div>
  );
};
